import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { memo, useRef } from 'react';
import CurrencyInput, { CurrencyInputProps } from 'react-currency-input-field';
import { Input } from '../ui/input';

export interface InputCurrencyProps
    extends Omit<CurrencyInputProps, 'onChange' | 'value'> {
    value?: number;
    onChange?: (value: number | undefined) => void;
    wrapperClassName?: string;
}

export const InputCurrency = memo((props: InputCurrencyProps) => {
    const { value, onChange, wrapperClassName, ...inputProps } = props;

    const step = props.step ?? 10000;

    const onClickUp = () => {
        const newValue = (value ?? 0) + step;
        if (typeof inputProps.max === 'number' && newValue > inputProps.max) {
            console.log('up-go-hree');
            onChange?.(inputProps.max);
        }
        onChange?.(newValue);
    };
    const onClickDown = () => {
        const newValue = (value ?? 0) - step;
        if (newValue < 0) {
            onChange?.(0);
        } else {
            onChange?.(newValue);
        }
    };

    const holdTimer = useRef<NodeJS.Timeout | null>(null);
    const intervalTimer = useRef<NodeJS.Timeout | null>(null);

    const handleMouseDown = (action: 'up' | 'down') => {
        holdTimer.current = setTimeout(() => {
            intervalTimer.current = setInterval(() => {
                let func = action === 'up' ? onClickUp : onClickDown;
                func();
            }, 200);
        }, 100);
    };

    const handleMouseUp = () => {
        holdTimer.current && clearTimeout(holdTimer.current);
        intervalTimer.current && clearInterval(intervalTimer.current);
    };

    return (
        <div className={cn('group relative', wrapperClassName)}>
            <CurrencyInput
                value={value}
                onValueChange={(_, __, values) => {
                    // console.log(value, name, values)
                    if (values) {
                        const { float } = values;
                        onChange?.(float ?? undefined);
                    }
                }}
                allowNegativeValue={false}
                intlConfig={{ locale: 'vi-VN', currency: 'VND' }}
                {...inputProps}
                step={step} // default step 10k
                customInput={Input}
                className={cn('pr-7', props.className)}
            />

            {!inputProps.disabled && (
                <div className='absolute inset-y-0 right-0 top-0 w-0 overflow-hidden rounded-r-lg transition-all group-hover:w-6 group-hover:p-0.5'>
                    <div className='flex h-full flex-col items-center justify-center border-l border-gray-200 pr-0.5 text-gray-500 hover:text-gray-800'>
                        <button
                            tabIndex={-1}
                            type='button'
                            className='flex flex-1 items-end border-b border-gray-200 px-1 py-0.5 pb-0 hover:bg-gray-100'
                            onClick={onClickUp}
                            onMouseDown={() => handleMouseDown('up')}
                            onMouseUp={handleMouseUp}
                        >
                            <ChevronUp size={14} strokeWidth={2.5} />
                        </button>
                        <button
                            tabIndex={-1}
                            type='button'
                            className='flex flex-1 items-start px-1 py-0.5 pt-0 hover:bg-gray-100'
                            onClick={onClickDown}
                            onMouseDown={() => handleMouseDown('down')}
                            onMouseUp={handleMouseUp}
                        >
                            <ChevronDown size={14} strokeWidth={2.5} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
});

InputCurrency.displayName = 'InputCurrency';
